import React, { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import PlayerTimelineButton from './PlayerTimelineButton';
import { getPlayerTimeFromSegmentTime } from '../timeUtils';
import { getAllSegments } from '../clipUpdateDataUtilsWithSplits/compositeUtils';
import { CLIP_UPDATE_TYPE } from '../clipUpdateDataUtils/constants';
import { updateClipData } from '../clipUpdateDataUtils';
import { filterAndAdjustElementTracksForSubClip } from '../playerConfigUtil';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { CLIP_MIN_DURATION } from '@/components/molecules/Transcript/constants';
import { getClipFPS } from '@/libs/clipUtils';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { Clip } from '@/domains/asset';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import { VideoSegment } from '@/App/remotion/MainPlayer/types/Segment/VideoSegment';

export default function CreateClipSelected() {
  const { createClip } = useTranscriptPage();
  const { clipData } = useClipsContext();
  const [isCreatingClip, setIsCreatingClip] = useState(false);
  const { startTime, endTime, isSelecting, startSegmentId, endSegmentId } = useSyncExternalStore(
    transcriptTimelineSelectionStore.subscribe,
    transcriptTimelineSelectionStore.getSnapshot
  );

  const shouldShowCreateClipButton = useMemo(() => {
    if (isSelecting) {
      return false;
    }
    if (startTime === null || endTime === null || startSegmentId === null || endSegmentId === null) {
      return false;
    }
    const allSegments = getAllSegments(clipData);

    const startIndex = allSegments.findIndex(s => s.id === startSegmentId);
    const endIndex = allSegments.findIndex(s => s.id === endSegmentId);

    const selectedSegments = allSegments.slice(startIndex, endIndex + 1);

    const hasCompositeSegment = selectedSegments.some(segment => segment.type === SegmentType.COMPOSITE);

    if (!hasCompositeSegment) {
      return false;
    }
    const startSegment = allSegments[startIndex];
    if (!startSegment) {
      return false;
    }
    const endSegment = allSegments[endIndex];
    if (!endSegment) {
      return false;
    }

    if (startSegmentId === 'intro' || startSegmentId === 'outro') {
      return startTime === 0;
    }

    if (endSegmentId === 'intro' || endSegmentId === 'outro') {
      return endTime === (endSegment as VideoSegment).segment_metadata?.maxVideoDuration;
    }

    return true;
  }, [isSelecting, startTime, endTime, startSegmentId, endSegmentId, clipData]);

  const clickHandler = useCallback(async () => {
    try {
      setIsCreatingClip(true);
      const additionalParams: Record<string, any> = {};
      if (isSplitsInTimelineEnabled(clipData)) {
        const allSegments = getAllSegments(clipData);

        const lastSegment = allSegments[allSegments.length - 1];

        const startSegment = allSegments.find(segment => segment.id === startSegmentId);
        if (!startSegment) {
          return;
        }
        const endSegment = allSegments.find(segment => segment.id === endSegmentId);
        if (!endSegment) {
          return;
        }

        const fps = getClipFPS(clipData);
        const lastSegmentEndTime =
          lastSegment.type === SegmentType.COMPOSITE || lastSegment.type === SegmentType.VIDEO
            ? lastSegment.segment_metadata?.maxVideoDuration
            : lastSegment.toFrame / fps;

        if (startTime === null || endTime === null) {
          return;
        }

        const playerConfigAfterForClip = updateClipData(
          {
            clipId: clipData.id,
            actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
            startTime: endTime,
            startSegmentId: endSegmentId || '',
            endSegmentId: lastSegment.id || '',
            endTime: lastSegmentEndTime || 0,
            deleteType: 'MANUAL'
          },
          {
            currentClipData: updateClipData(
              {
                clipId: clipData.id,
                actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
                startTime: 0,
                startSegmentId: allSegments[0].id,
                endSegmentId: startSegmentId || '',
                endTime: startTime,
                deleteType: 'MANUAL'
              },
              {
                skipStoreAndBackendUpdate: true
              }
            ),
            skipStoreAndBackendUpdate: true
          }
        ) as Clip;
        const playerStartFrame = getPlayerTimeFromSegmentTime(startTime, clipData, startSegmentId || '') * fps;
        const playerEndFrame = getPlayerTimeFromSegmentTime(endTime, clipData, endSegmentId || '') * fps;

        const playerConfig = playerConfigAfterForClip.asset_metadata.player_config;
        const originalPlayerConfig = clipData.asset_metadata.player_config;
        additionalParams.player_config = filterAndAdjustElementTracksForSubClip(
          playerConfig,
          originalPlayerConfig,
          playerStartFrame,
          playerEndFrame
        );
      }

      await createClip(startTime, endTime, {
        edits: clipData.asset_metadata.edits,
        deletes: clipData.asset_metadata.deletes || {},
        scenes: clipData.asset_metadata.scenes || [],
        fps: getClipFPS(clipData),
        additionalParams
      });
    } finally {
      setIsCreatingClip(false);
    }
  }, [clipData, createClip, endSegmentId, endTime, startSegmentId, startTime]);

  if (
    !shouldShowCreateClipButton ||
    startTime === null ||
    endTime === null ||
    startSegmentId === null ||
    endSegmentId === null
  ) {
    return <></>;
  }
  const clipDuration =
    getPlayerTimeFromSegmentTime(endTime, clipData, endSegmentId) -
    getPlayerTimeFromSegmentTime(startTime, clipData, startSegmentId);

  let tooltipContent = 'Create Clip';
  if (isCreatingClip) {
    tooltipContent = 'Creating clip...';
  }
  if (clipDuration < CLIP_MIN_DURATION) {
    tooltipContent = `Clip duration must be at least ${CLIP_MIN_DURATION} seconds`;
  }

  const isDisabled = isCreatingClip || clipDuration < CLIP_MIN_DURATION;
  return (
    <PlayerTimelineButton
      tooltipTitle={tooltipContent}
      buttonId="create-clip-button"
      onClick={clickHandler}
      isDisabled={isDisabled}
      isLoading={isCreatingClip}
      iconName="IconMovie"
    />
  );
}
